import { AddContactModal } from '@components/AddContactModal';
import { AddWhatsappModal } from '@components/AddWhatsappModal';
import { ChangeBusinessTypeModal } from '@components/ChangeBusinessTypeModal';
import { ContactLink } from '@components/ContactLink';
import { DeleteLeadModal } from '@components/DeleteLeadModal';
import { EmptyState } from '@components/EmptyState';
import { GifPreview } from '@components/GifPreview';
import { EnrichmentJsonModal } from '@components/EnrichmentJsonModal';
import { Header } from '@components/Header';
import { IconButton } from '@components/IconButton';
import {
  IconCheckCircle,
  IconDoNotDisturb,
  IconFilter,
  IconProfileOff,
  IconSearch,
  SelectFromListIcon,
} from '@components/icons';
import { Input } from '@components/Input';
import { LeadTypeViewHeader } from '@components/LeadTypeViewHeader';
import { SelectableRow } from '@components/SelectableRow';
import { SelectFilter } from '@components/SelectFilter';
import { TableHeaderCell } from '@components/TableHeaderCell';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AddMenu } from './AddMenu';
import { LeadActionBar } from './LeadActionBar';
import { ProfilesSection } from './ProfilesSection';
import { useManageLeads } from './useManageLeads';
import {
  contactInfoOptions,
  getContactsFromLead,
  getPlatform,
  getWhatsAppFromLead,
  leadStatusOptions,
  technologyOptions,
} from './utils';
import styles from './ManageLeadsPage.module.scss';

export const ManageLeadsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    // Filters
    leadStatusFilter,
    cityFilter,
    technologyFilter,
    contactInfoFilter,
    openFilter,
    businessTypeOptions,
    typeOptions,
    typesFilter,
    cityOptions,
    hasFilters,
    handleLeadStatusChange,
    handleTypesChange,
    handleCityChange,
    handleTechnologyChange,
    handleContactInfoChange,
    handleFilterOpenChange,
    handleClearFilters,

    // Site list filter
    siteFilterText,
    setSiteFilterText,
    isFilterExpanded,
    setIsFilterExpanded,
    filterInputRef,
    handleFilterBlur,
    handleFilterClear,
    handleFilterKeyDown,

    // Leads
    leads,
    leadsLoading,
    displayedLeads,

    // Selected lead
    selectedSiteId,
    setSelectedSiteId,
    selectedLead,
    selectedLeadInfo,
    currentIndex,
    totalNavigableCount,
    handlePrevious,
    handleNext,

    // Lead status flags
    isSelectedLeadNotOk,
    leadRating,
    canDeleteLead,
    isSelectedLeadOnboarding,
    isSelectedLeadOnboarded,
    isOnboardDisabled,
    contactedCount,
    hasContactedAny,

    // GIF
    isGifRegenerating,
    gifCacheBustKey,
    handleRegenerateGif,

    // Apollo data
    apolloEventsMap,
    contactedPersonIds,
    sequences,
    reachOutStates,

    // Add menu
    isAddMenuOpen,
    setIsAddMenuOpen,

    // Action handlers
    handleLogout,
    handleOnboardSite,
    handleViewDemo,
    handleRatingChange,
    handleDeleteLead,
    handleDeleteContact,
    handleEnrichProfile,
    handleAddToContacts,
    handleReachOutWithSequence,
    handleReachOutWhatsApp,
    handleSaveWhatsappNumber,

    // Modal state
    jsonModalPerson,
    setJsonModalPerson,
    isAddContactModalOpen,
    setIsAddContactModalOpen,
    isWhatsappModalOpen,
    setIsWhatsappModalOpen,
    isDeleteLeadModalOpen,
    setIsDeleteLeadModalOpen,
    deleteContactTarget,
    setDeleteContactTarget,
    isChangeBusinessTypeModalOpen,
    setIsChangeBusinessTypeModalOpen,
    handleChangeBusinessType,

    addManualContactForLead,
  } = useManageLeads();

  const hasNoCombinedData =
    selectedLead &&
    (!selectedLead.contacts?.apollo?.people ||
      selectedLead.contacts.apollo.people.length === 0) &&
    (!selectedLead.contacts?.apollo?.people_enriched ||
      selectedLead.contacts.apollo.people_enriched.length === 0) &&
    !getWhatsAppFromLead(selectedLead) &&
    getContactsFromLead(selectedLead).length === 0;

  return (
    <div className={styles.page}>
      <Header
        showLogout
        showTabs
        tabs={[
          { label: 'Overview', active: false, onClick: () => navigate('/') },
          { label: 'Generate Leads', active: false, onClick: () => navigate('/leads') },
          { label: 'Manage Leads', active: true },
        ]}
        onLogout={handleLogout}
      />

      <main className={styles.main}>
        <div className={styles.wrapper}>
          {/* Page Header with Filters */}
          <div className={styles.pageHeader}>
            <h1 className={styles.title}>Manage Leads</h1>
            <div className={styles.filters}>
              <SelectFilter
                label="Lead Status"
                items={leadStatusOptions}
                multiSelect
                selectedValues={leadStatusFilter}
                onSelectionChange={handleLeadStatusChange}
                isOpen={openFilter === 'leadStatus'}
                onOpenChange={handleFilterOpenChange('leadStatus')}
                filterable
                showAndNotice
              />
              <SelectFilter
                label="Types"
                items={typeOptions}
                multiSelect
                selectedValues={typesFilter}
                onSelectionChange={handleTypesChange}
                isOpen={openFilter === 'businessType'}
                onOpenChange={handleFilterOpenChange('businessType')}
                filterable
              />
              <SelectFilter
                label="City"
                items={cityOptions}
                multiSelect
                selectedValues={cityFilter}
                onSelectionChange={handleCityChange}
                isOpen={openFilter === 'cities'}
                onOpenChange={handleFilterOpenChange('cities')}
                filterable
              />
              <SelectFilter
                label="Technology"
                items={technologyOptions}
                multiSelect
                selectedValues={technologyFilter}
                onSelectionChange={handleTechnologyChange}
                isOpen={openFilter === 'technology'}
                onOpenChange={handleFilterOpenChange('technology')}
                filterable
              />
              <SelectFilter
                label="Contacts"
                items={contactInfoOptions}
                multiSelect
                selectedValues={contactInfoFilter}
                onSelectionChange={handleContactInfoChange}
                isOpen={openFilter === 'contactInfo'}
                onOpenChange={handleFilterOpenChange('contactInfo')}
                filterable
              />
            </div>
          </div>

          {/* Main Content */}
          <div className={styles.content}>
            {!hasFilters ? (
              // Init state — no filters selected
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  icon={<IconFilter size={82} color="var(--color-primary-base)" />}
                  title="Select a filter to begin"
                  subtitle="Filter by business type, geography, technology, or contact information to see your leads."
                  hidePrimaryActionLeadingIcon
                />
              </div>
            ) : leadsLoading ? (
              // Loading state
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  icon={<IconSearch size={82} color="var(--color-primary-base)" />}
                  title="Loading leads..."
                  subtitle="Please wait while we fetch your leads."
                  hidePrimaryActionLeadingIcon
                />
              </div>
            ) : leads.length === 0 ? (
              // No results state
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  icon={<IconSearch size={82} color="var(--color-primary-base)" />}
                  title="No leads match your filters"
                  subtitle="Try adjusting or clearing one or more filters to see results."
                  primaryActionText="Clear All Filters"
                  onPrimaryAction={handleClearFilters}
                  hidePrimaryActionLeadingIcon
                />
              </div>
            ) : (
              // Results state — two column layout
              <div className={styles.resultsLayout}>
                {/* Left sidebar — site list */}
                <div className={styles.siteList}>
                  <div className={styles.filterRow} ref={filterInputRef}>
                    {isFilterExpanded ? (
                      <Input
                        value={siteFilterText}
                        onChange={setSiteFilterText}
                        placeholder={t('common.filter')}
                        leadingIcon={<IconFilter size={16} />}
                        clearable
                        onBlur={handleFilterBlur}
                        onClear={handleFilterClear}
                        onKeyDown={handleFilterKeyDown}
                      />
                    ) : (
                      <>
                        <span
                          className={`${styles.countLabel}${siteFilterText ? ` ${styles.countFiltered}` : ''}`}
                        >
                          {siteFilterText
                            ? t('manageLeads.leadCountFiltered', {
                                filtered: displayedLeads.length,
                                total: leads.length,
                                count: leads.length,
                              })
                            : t('manageLeads.leadCount', { count: displayedLeads.length })}
                        </span>
                        <IconButton
                          size="small"
                          aria-label={t('common.filter')}
                          onClick={() => setIsFilterExpanded(true)}
                          className={siteFilterText ? styles.filterActive : undefined}
                        >
                          <IconFilter size={14} />
                        </IconButton>
                      </>
                    )}
                  </div>
                  <div className={styles.siteListHeader}>
                    <TableHeaderCell title="Site Name" className={styles.siteListHeaderName} />
                    <TableHeaderCell title="URLs" className={styles.siteListHeaderUrls} />
                  </div>
                  <div className={styles.siteListItems}>
                    {displayedLeads.length === 0 && siteFilterText ? (
                      <div className={styles.noResultsState}>
                        <EmptyState
                          size="minimal"
                          icon={<IconFilter size={40} />}
                          title="No results found"
                          subtitle="Try a different search term"
                        />
                      </div>
                    ) : (
                      displayedLeads.map((lead) => {
                        // Terminal lead.status (Lost / Converted) overrides
                        // the manual notOK/markedOK rating on the row.
                        const isConverted = lead.status === 'converted';
                        const isLost = lead.status === 'lost';
                        const tone = isConverted
                          ? 'success'
                          : isLost
                            ? 'danger'
                            : undefined;
                        const statusIcon = isConverted ? (
                          <IconCheckCircle
                            size={18}
                            color="var(--color-semantic-success)"
                          />
                        ) : isLost ? (
                          <IconDoNotDisturb
                            size={18}
                            color="var(--color-semantic-danger)"
                          />
                        ) : lead.notOK === true ? (
                          <IconDoNotDisturb size={18} />
                        ) : lead.markedOK === true ? (
                          <IconCheckCircle
                            size={18}
                            color="var(--color-semantic-success)"
                          />
                        ) : undefined;
                        return (
                          <SelectableRow
                            key={lead.id}
                            label={lead.name}
                            subtitle={lead.url}
                            value={lead.urlCount || 0}
                            selected={lead.id === selectedSiteId}
                            onClick={() => {
                              setSelectedSiteId(lead.id);
                              setIsAddMenuOpen(false);
                            }}
                            muted={!tone && lead.notOK === true}
                            tone={tone}
                            statusIcon={statusIcon}
                          />
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Right side — site details or empty state */}
                <div className={styles.siteDetails}>
                  {!selectedLead ? (
                    <div className={styles.noSiteSelected}>
                      <EmptyState
                        icon={<SelectFromListIcon width={82} height={60} />}
                        title="No Site Selected"
                        subtitle="Select a site on the left to start exploring leads."
                        hidePrimaryActionLeadingIcon
                      />
                    </div>
                  ) : (
                    <>
                      <div className={styles.siteDetailsHeader}>
                        <LeadTypeViewHeader
                          title={selectedLead.name}
                          date={
                            selectedLead.created
                              ? `Created ${selectedLead.created.toDate().toLocaleDateString()}`
                              : 'Unknown'
                          }
                          showLeads={false}
                          showOpenInNew
                          openInNewLabel={
                            selectedLead.url
                              ? selectedLead.url.replace(/^https?:\/\//, '').replace(/\/$/, '')
                              : selectedLead.name
                          }
                          onOpenInNew={() =>
                            window.open(
                              selectedLead.url || `https://${selectedLead.name}`,
                              '_blank'
                            )
                          }
                          showMetadata
                          totalUrls={selectedLead.urlCount || 0}
                          platform={getPlatform(selectedLead)}
                          showNavButtons
                          currentIndex={currentIndex}
                          totalCount={totalNavigableCount}
                          onPrevious={handlePrevious}
                          onNext={handleNext}
                          businessTypeName={selectedLeadInfo.businessTypeName}
                          cityName={selectedLeadInfo.cityName}
                          onBusinessTypeClick={
                            selectedLead.scope?.business_type
                              ? () =>
                                  navigate(
                                    `/leads?type=${encodeURIComponent(selectedLead.scope!.business_type!)}`
                                  )
                              : undefined
                          }
                          listName={selectedLeadInfo.sourceListName}
                          onListClick={
                            selectedLeadInfo.sourceListId
                              ? () => {
                                  // List-sourced lead (SC-2189): clear all filters and
                                  // switch to this list's filtered view. Navigate updates
                                  // the URL for shareability; state updates directly since
                                  // same-route navigation won't re-run the deep-link init.
                                  const listId = selectedLeadInfo.sourceListId!;
                                  handleClearFilters();
                                  handleTypesChange([`list:${listId}`]);
                                  navigate(`/manage-leads?list=${encodeURIComponent(listId)}`, {
                                    replace: true,
                                  });
                                }
                              : undefined
                          }
                          onAddBusinessType={
                            !selectedLead.scope?.business_type
                              ? () => setIsChangeBusinessTypeModalOpen(true)
                              : undefined
                          }
                          onChangeBusinessType={
                            selectedLead.scope?.business_type
                              ? () => setIsChangeBusinessTypeModalOpen(true)
                              : undefined
                          }
                          siteId={isSelectedLeadOnboarded ? selectedLead.site_id : undefined}
                          titleAddon={
                            isSelectedLeadOnboarded && selectedLead.site_id ? (
                              <GifPreview
                                siteId={selectedLead.site_id}
                                onRegenerate={handleRegenerateGif}
                                isRegenerating={isGifRegenerating}
                                cacheBustKey={gifCacheBustKey}
                              />
                            ) : undefined
                          }
                        />
                      </div>

                      <LeadActionBar
                        isNotOk={isSelectedLeadNotOk}
                        isOnboarded={isSelectedLeadOnboarded}
                        isOnboarding={isSelectedLeadOnboarding}
                        isOnboardDisabled={isOnboardDisabled}
                        hasContactedAny={hasContactedAny}
                        contactedCount={contactedCount}
                        canDeleteLead={canDeleteLead}
                        leadRating={leadRating}
                        leadStatus={selectedLead?.status}
                        onViewDemo={handleViewDemo}
                        onOnboardSite={handleOnboardSite}
                        onDeleteLead={() => setIsDeleteLeadModalOpen(true)}
                        onRatingChange={handleRatingChange}
                      />

                      <div
                        className={`${styles.detailsContent} ${isSelectedLeadNotOk ? styles.disabled : ''}`}
                      >
                        {hasNoCombinedData ? (
                          // Both profiles AND social contacts are empty
                          <div className={styles.combinedEmptyState}>
                            <div className={styles.combinedEmptyStateHeader}>
                              <AddMenu
                                isOpen={isAddMenuOpen}
                                onOpenChange={setIsAddMenuOpen}
                                onAddContact={() => setIsAddContactModalOpen(true)}
                                onAddWhatsApp={() => setIsWhatsappModalOpen(true)}
                              />
                            </div>
                            <EmptyState
                              icon={
                                <IconProfileOff
                                  size={82}
                                  color="var(--color-primary-light)"
                                />
                              }
                              title="No contact data found"
                              subtitle="We haven't found any contacts, WhatsApp, or social profiles for this lead yet."
                              hidePrimaryActionLeadingIcon
                            />
                          </div>
                        ) : (
                          <>
                            <ProfilesSection
                              lead={selectedLead}
                              selectedSiteId={selectedSiteId!}
                              apolloEventsMap={apolloEventsMap}
                              contactedPersonIds={contactedPersonIds}
                              isAddMenuOpen={isAddMenuOpen}
                              onAddMenuOpenChange={setIsAddMenuOpen}
                              sequences={sequences}
                              reachOutStates={reachOutStates}
                              onAddContact={() => setIsAddContactModalOpen(true)}
                              onAddWhatsApp={() => setIsWhatsappModalOpen(true)}
                              onViewJson={(name, data) => setJsonModalPerson({ name, data })}
                              onEnrichProfile={handleEnrichProfile}
                              onAddToContacts={handleAddToContacts}
                              onReachOutWithSequence={handleReachOutWithSequence}
                              onReachOutWhatsApp={handleReachOutWhatsApp}
                              onEditWhatsApp={() => setIsWhatsappModalOpen(true)}
                              onDeleteContact={(personId, name) =>
                                setDeleteContactTarget({ personId, name })
                              }
                            />

                            {/* Contacts column — social links */}
                            <div className={styles.contactsColumn}>
                              {getContactsFromLead(selectedLead).length > 0 ? (
                                getContactsFromLead(selectedLead).map((contact) => (
                                  <ContactLink
                                    key={contact.id}
                                    provider={contact.provider}
                                    label={contact.label}
                                    href={contact.href}
                                  />
                                ))
                              ) : (
                                <EmptyState
                                  size="minimal"
                                  icon={
                                    <IconProfileOff
                                      size={40}
                                      color="var(--color-primary-light)"
                                    />
                                  }
                                  title="No social profiles found"
                                />
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <EnrichmentJsonModal
        isOpen={!!jsonModalPerson}
        onClose={() => setJsonModalPerson(null)}
        personName={jsonModalPerson?.name ?? ''}
        data={jsonModalPerson?.data ?? {}}
      />

      <AddContactModal
        isOpen={isAddContactModalOpen}
        onClose={() => setIsAddContactModalOpen(false)}
        onAdd={addManualContactForLead}
      />

      <AddWhatsappModal
        isOpen={isWhatsappModalOpen}
        onClose={() => setIsWhatsappModalOpen(false)}
        onSave={handleSaveWhatsappNumber}
        defaultNumber={selectedLead ? getWhatsAppFromLead(selectedLead)?.number || '' : ''}
      />

      <DeleteLeadModal
        isOpen={isDeleteLeadModalOpen}
        onClose={() => setIsDeleteLeadModalOpen(false)}
        leadName={selectedLead?.name || ''}
        onConfirm={handleDeleteLead}
      />

      <DeleteLeadModal
        isOpen={deleteContactTarget !== null}
        onClose={() => setDeleteContactTarget(null)}
        leadName={deleteContactTarget?.name || ''}
        onConfirm={handleDeleteContact}
        title={t('deleteContact.title')}
        confirmMessage={t('deleteContact.confirmMessage', {
          contactName: deleteContactTarget?.name || '',
        })}
      />

      <ChangeBusinessTypeModal
        isOpen={isChangeBusinessTypeModalOpen}
        onClose={() => setIsChangeBusinessTypeModalOpen(false)}
        currentSlug={selectedLead?.scope?.business_type ?? ''}
        availableTypes={businessTypeOptions}
        onSave={handleChangeBusinessType}
      />
    </div>
  );
};
